/* eslint-disable */
// @ts-nocheck
import { getDefaultFormState } from '@rjsf/utils';
import TForm, { FormProps, IChangeEvent } from '@rjsf/core';
import { customizeValidator } from '@rjsf/validator-ajv8';
import { useDebounceFn, useRequest } from 'ahooks';
import merge from 'deepmerge';
import { omit, pick } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import React, { forwardRef, memo, Ref, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Form from '@/components/core/Form';
import { localization } from '@/components/core/Form/utils';
import User from '@/domain/core/User';
import { patchUiSchema } from '@/features/uiSchemasLibrary/utils';
import { TUserProfile } from '@/types/user';
import { backendApi } from '@/utils/api';
import { Loader2 } from 'lucide-react';
import { getUiSchema } from './schema';
import { fields } from './fields'
import { SelectionDTO } from '@/features/admin/selections/dtos/selection';
import { fieldGroups } from '@/features/templates/TemplatesEditForm/consts';

// TODO: Move to utils
const overwriteMerge = (_, sourceArray: Array<unknown>) => sourceArray;

export const customFormats = {
	'iri': new RegExp(
		"^" +
		// protocol identifier (optional)
		// short syntax // still required
		"https?:\\/\\/" +
		// host & domain names, may end with dot
		// can be replaced by a shortest alternative
		// (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
		"(?:" +
		"(?:" +
		"[a-z0-9\\u00a1-\\uffff]" +
		"[a-z0-9\\u00a1-\\uffff_-]{0,62}" +
		")?" +
		"[a-z0-9\\u00a1-\\uffff]\\." +
		")+" +
		// TLD identifier name, may end with dot
		"(?:[a-z\\u00a1-\\uffff]{2,}\\.?)" +
		// port number (optional)
		"(?::\\d{2,5})?" +
		// resource path (optional)
		"(?:[/?#]\\S*)?" +
		"$", "i"
	),
};

const getInitiator = async (initiatorId: number): Promise<TUserProfile | undefined> => {
	if (initiatorId) {
		const { data: initiator } = await backendApi.get(`/v2/users/${initiatorId}`);

		return initiator?.profile;
	}

	return Promise.reject();
}

type IProps = Omit<FormProps<T>, 'validator'> & {
	onFormDataChange?: (formData: T) => void;
	resetKey?: number | string;
	selection: SelectionDTO | undefined;
};

const ApplicationsEditForm = memo(
	forwardRef(
		<T, S, F>(
			{
				id = 'form',
				schema,
				onSubmit,
				onError,
				formData,
				formContext,
				transformErrors,
				selection,
				onFormDataChange,
				resetKey,
			}: Omit<FormProps<T>, 'validator'>,
			ref: Ref<TForm<T>>
		) => {
			const { t, i18n } = useTranslation();

			const uiSchema = useMemo(() => {
				return getUiSchema(t);
			}, [i18n.language]);
			const [patchedSchema, setPatchedSchema] = useState(schema);
			useEffect(() => {
				setPatchedSchema(schema);
			}, [schema]);

			const patchedUiSchema = useMemo(() => patchUiSchema(uiSchema, schema, formContext?.groups), [uiSchema, schema]);
			const validator = useMemo(() => {
				return customizeValidator({customFormats}, localization(t))
			}, [t]);

			const filteredDefaultFormData = useMemo(() => {
				const defaultFormData = getDefaultFormState(validator, schema, formData, schema);
				const fileArrayFields = fieldGroups.find(g => g.groupName === 'upload')?.fieldNames || [];

				return Object.keys(defaultFormData).reduce((acc, key) => {
					return {
						...acc,
						[key]: Array.isArray(defaultFormData[key]) && fileArrayFields.includes(key) ? defaultFormData[key].filter(v => v != null) : defaultFormData[key]
					}
				}, {});
			}, [validator, schema, formData]);

			const [formSubmittedOnce, setFormSubmittedOnce] = useState<boolean>(false);
			const [currentFormData, setCurrentFormData] = useState<FormProps<T>['formData']>(filteredDefaultFormData ?? {} as T);
			const [extraErrors, setExtraErrors] = useState<Record<any, any>>({});

            const {
				loading,
				data: initiator = User?.profile
			} = useRequest(() => getInitiator(formContext.initiatorId), {
				refreshDeps: [formContext.initiatorId],
				onError: () => {
					console.error(t('application.initiatorLoadError'));
				}
			});

			const { run: useDebouncedHandleChange, cancel: cancelDebouncedHandleChange } = useDebounceFn(
				(formData: FormProps<T>['formData'], initiator: TUserProfile | undefined) => {
					const localFormData = cloneDeep(formData);
					let nextFormData = {} as T;
					// TODO от этого костыля нужно избавляться, из-за этого merge.all не работают omitExtraData и liveOmit
					setCurrentFormData((prev) => {
						nextFormData = merge.all(
							[
								omit(prev, [
									'businessPlan',
									'finansModel',
									'exportProposal',
									'exportProposalFile',
								]),
								localFormData,
							],
							{ arrayMerge: overwriteMerge }
						) as T;

						return nextFormData;
					});

					onFormDataChange?.(nextFormData);
				},
				{
					wait: 100,
				}
			);

			useEffect(() => {
				cancelDebouncedHandleChange();
				setCurrentFormData(getDefaultFormState(validator, schema, formData, schema) ?? ({} as T));
			}, [cancelDebouncedHandleChange, formData, resetKey, schema, validator]);

            const processDependentFields = (formData: IChangeEvent<T>['formData'], fieldId: string) => {
                const clearDirectionAndSubDirection = () => {
                    const newSchema = {...schema};
                    newSchema.required = newSchema.required?.filter(field => field !== 'subDirection' && field !== 'subSubDirection');
                    newSchema.properties = {...newSchema.properties};
                    delete newSchema.properties.subDirection;
                    delete newSchema.properties.subSubDirection;
                    setPatchedSchema(newSchema);
                }
                const clearSubSubDirection = () => {
                    const newSchema = {...schema};
                    newSchema.required = newSchema.required?.filter(field => field !== 'subSubDirection');
                    newSchema.properties = {...newSchema.properties};
                    delete newSchema.properties.subSubDirection;
                    setPatchedSchema(newSchema);
                }
                if ('root_direction' === fieldId) {
                    if (formData && 'direction' in formData && selection) {
                        let subDirections = [];
                        if (formData.direction) {
                            const direction = selection.directions?.find(direction => direction.id === formData.direction);
                            let hasSubDirections = false;
                            if (direction && direction?.subDirection?.length) {
                                hasSubDirections = true;
                                subDirections = direction.subDirection;
                            }
                            if (!hasSubDirections) {
                                clearDirectionAndSubDirection();
                            } else {
                                if (!patchedSchema.properties.subDirections) {
                                    setPatchedSchema(schema);
                                }
                            }
                        } else {
                            clearDirectionAndSubDirection();
                        }
                        if (formData.subDirection) {
                            if (!subDirections.find(d => d.id == formData.subDirection)) {
                                formData.subDirection = undefined;
                            }
                        } else {
                            formData.subDirection = undefined;
                        }
                    }
                    if (formData && !formData.subDirection) {
                        formData.subSubDirection = undefined;
                    }
                }
                if ('root_subDirection' === fieldId) {
                    if (formData) {
                        let subDirections = [];
                        if (formData.direction && formData.subDirection && selection) {
                            const direction = selection.directions?.find(direction => direction.id === formData.direction);
                            const subDirection = direction.subDirection?.find(direction => direction.id === formData.subDirection);
                            let hasSubSubDirections = false;
                            if (subDirection && subDirection?.subDirection?.length) {
                                hasSubSubDirections = true;
                                subDirections = subDirection?.subDirection;
                            }
                            if (!hasSubSubDirections) {
                                clearSubSubDirection();
                            } else {
                                if (!patchedSchema.properties.subSubDirection) {
                                    setPatchedSchema(schema);
                                }
                            }
                        } else {
                            clearSubSubDirection();
                        }
                        if (formData.subDirection) {
                            if (!subDirections.find(d => d.id == formData.subSubDirection)) {
                                formData.subSubDirection = undefined;
                            }
                        } else {
                            formData.subSubDirection = undefined;
                        }
                    }
                }
                if (fieldId.startsWith('root_currentRegions')) {
                    if (formData && 'currentRegions' in formData) {
                        // Если есть одинаковые регионы, добавляем ошибку
                        const values = (formData.currentRegions || []).filter((v: any) => v != null && v !== '');
                        const numbers = values.map((v: any) => typeof v === 'number' ? v : Number(v)).filter((v: any) => !Number.isNaN(v));
                        const hasDuplicates = numbers.length > new Set(numbers).size;
                        if (hasDuplicates) {
                            const message = t('form.validation.duplicate');
                            setExtraErrors(prev => ({
                                ...(prev || {}),
                                currentRegions: {
                                    __errors: [message]
                                }
                            }));
                        } else {
                            setExtraErrors(prev => {
                                delete prev.currentRegions;
                                return prev;
                            })
                        }
                    }
                }
            };

			const handleChange = useCallback(
				({ formData }: IChangeEvent<T>, fieldId: string) => {
                    processDependentFields(formData, fieldId);
					useDebouncedHandleChange(formData, initiator);
				},
				[initiator, selection, schema]
			);

            useEffect(() => {
                setPatchedSchema(schema);
                processDependentFields(formData, 'root_direction');
                processDependentFields(formData, 'root_subDirection');
            }, [schema]);

			const handleSubmit = useCallback(async (formProps: FormProps<T>, e) => {
					setFormSubmittedOnce(true);
					onSubmit(formProps, e);
				},
				[onSubmit, patchedSchema]
			);

			const handleError = useCallback((errors) => {
					setFormSubmittedOnce(true);
					if (typeof onError === 'function') {
						onError(errors);
					}
				},
				[onError]
			);

			return loading ?
					<div className="absolute flex inset-0 items-center justify-center">
						<Loader2 className="animate-spin" />
					</div>
				 :
					<Form
						id={id}
						ref={ref}
						schema={patchedSchema}
						uiSchema={patchedUiSchema}
						formData={currentFormData}
						fields={fields}
						validator={validator}
						extraErrors={extraErrors}
						onChange={handleChange}
						onSubmit={handleSubmit}
						onError={handleError}
						formContext={{
							...formContext,
							formData: pick(currentFormData, ['direction', 'subDirection', 'subSubDirection'])
						}}
						transformErrors={transformErrors}
						showErrorList="bottom"
						noHtml5Validate
						omitExtraData
						liveValidate={formSubmittedOnce}
					>
						{/* eslint-disable-next-line react/jsx-no-useless-fragment */}
						<></>
					</Form>
		}
	)
) as <T>(props: IProps, ref: Ref<TForm<T>>) => JSX.Element;

export { ApplicationsEditForm };
